import { glyphEm } from "./wrap-display-line";

/**
 * StoryTemplate title wrapping geometry, kept outside the renderer so projected
 * layouts and inspector pagination use the exact same authored title height.
 */
function wrapTitleLinesMirror(text: string, maxWidth: number, size: number, maxLines = 3): string[] {
  const widthOf = (value: string) => [...value].reduce((sum, ch) => sum + glyphEm(ch) * size, 0);
  const wrapSegment = (segment: string): string[] => {
    const words = segment.split(/(\s+)/).filter((word) => word.length > 0);
    const lines: string[] = [];
    let current = "";
    for (const word of words) {
      const candidate = current + word;
      if (current && widthOf(candidate) > maxWidth) {
        lines.push(current.trimEnd());
        current = word.trim().length === 0 ? "" : word;
      } else {
        current = candidate;
      }
    }
    if (current.trim().length > 0) lines.push(current.trimEnd());
    return lines;
  };
  const lines: string[] = [];
  for (const segment of (text ?? "").split("\n")) {
    const wrapped = wrapSegment(segment);
    if (wrapped.length) lines.push(...wrapped);
    else lines.push("");
  }
  while (lines.length > 1 && lines[lines.length - 1] === "") lines.pop();
  if (lines.length === 0) return [text ?? ""];
  if (lines.length > maxLines) return [...lines.slice(0, maxLines - 1), lines.slice(maxLines - 1).join(" ")];
  return lines;
}

/**
 * Extra vertical title height in StoryTemplate's 360px composition space.
 * The three-line renderer cap is reapplied without a cap to count any merged
 * final line's physical wraps, matching StoryTemplate's title layout exactly.
 */
export function storyTitleExtraForText(text: string, width: number, fontSize: number): number {
  if (!Number.isFinite(width) || width <= 0 || !Number.isFinite(fontSize) || fontSize <= 0) return 0;
  const titleLines = wrapTitleLinesMirror(text ?? "", width, fontSize);
  const physical = titleLines.reduce(
    (count, line) => count + Math.max(1, wrapTitleLinesMirror(line, width, fontSize, Number.MAX_SAFE_INTEGER).length),
    0,
  );
  return (physical - 1) * fontSize * 1.3;
}
